import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Percent } from 'lucide-react';
import { SHAREABLE_CATEGORIES } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, todayIso } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, DatePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { MoneyInput } from '@/components/MoneyInput';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { ShareChangeView } from './accounts-types';
import { parsePercent, type ShareRateKind } from './accounts-api';

export type ShareScope = 'service' | 'doctor_category' | 'doctor_service' | 'ward_rule';

export interface ShareTarget {
  scope: ShareScope;
  chargeItemId?: string;
  category?: string;
  doctorId?: string;
  wardChargeRuleId?: string;
  title: string;
  allowNoOwnRate: boolean;
}

const SCOPES: ShareScope[] = ['service', 'doctor_category', 'doctor_service', 'ward_rule'];

export function shareChangePath(target: ShareTarget, returnTo: string): string {
  const query = new URLSearchParams({ kind: 'share', scope: target.scope, title: target.title, returnTo });
  if (target.chargeItemId) query.set('chargeItemId', target.chargeItemId);
  if (target.category) query.set('category', target.category);
  if (target.doctorId) query.set('doctorId', target.doctorId);
  if (target.wardChargeRuleId) query.set('wardChargeRuleId', target.wardChargeRuleId);
  if (target.allowNoOwnRate) query.set('allowNoOwnRate', '1');
  return `/accounts/doctor-share/changes/new?${query.toString()}`;
}

export function categoryDefaultPath(returnTo: string): string {
  return `/accounts/doctor-share/changes/new?${new URLSearchParams({ kind: 'category', returnTo }).toString()}`;
}

function targetFrom(params: URLSearchParams): ShareTarget | null {
  const scope = params.get('scope');
  if (!scope || !SCOPES.includes(scope as ShareScope)) return null;
  return {
    scope: scope as ShareScope,
    chargeItemId: params.get('chargeItemId') ?? undefined,
    category: params.get('category') ?? undefined,
    doctorId: params.get('doctorId') ?? undefined,
    wardChargeRuleId: params.get('wardChargeRuleId') ?? undefined,
    title: params.get('title') ?? tr('billing', 'doctor_share'),
    allowNoOwnRate: params.get('allowNoOwnRate') === '1',
  };
}

export function ShareChangePage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/accounts/doctor-share');
  if (params.get('kind') === 'category') return <CategoryDefaultForm returnTo={returnTo} />;
  const target = targetFrom(params);
  if (!target) return <Alert variant="destructive">{tr('billing', 'that_record_could_not_be_opened')}</Alert>;
  return <ShareChangeForm target={target} returnTo={returnTo} />;
}

function useShareSaved(returnTo: string) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  return (pending: boolean) => {
    toast.success(pending ? tr('billing', 'waiting_for_a_second_person') : tr('billing', 'saved'));
    void queryClient.invalidateQueries({ queryKey: ['doctor-share-services'] });
    void queryClient.invalidateQueries({ queryKey: ['doctor-share-service-doctors'] });
    void queryClient.invalidateQueries({ queryKey: ['doctor-share-ward-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['doctor-share-overview'] });
    navigate(returnTo, { replace: true });
  };
}

function ShareChangeForm({ target, returnTo }: { target: ShareTarget; returnTo: string }) {
  const saved = useShareSaved(returnTo);
  const [kind, setKind] = useState<ShareRateKind>('pct');
  const [value, setValue] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [noOwnRate, setNoOwnRate] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const fixed = !noOwnRate && kind === 'fixed';
      const sharePct = noOwnRate || fixed ? null : parsePercent(value);
      const shareMinor = fixed ? amountMinor : null;
      if (!noOwnRate && !fixed && (sharePct === null || sharePct < 0 || sharePct > 100)) throw new Error('Enter a percentage between 0 and 100');
      if (fixed && (shareMinor === null || !Number.isInteger(shareMinor) || shareMinor < 0)) throw new Error(tr('billing', 'enter_an_amount_per_unit'));
      return post<ShareChangeView>('/accounts/doctor-share/changes', {
        scope: target.scope,
        chargeItemId: target.chargeItemId ?? null,
        category: target.category ?? null,
        doctorId: target.doctorId ?? null,
        wardChargeRuleId: target.wardChargeRuleId ?? null,
        sharePct,
        shareMinor,
        effectiveFrom,
        reason: reason.trim(),
        reference: reference.trim() || undefined,
      });
    },
    onSuccess: (row) => saved(row.status === 'pending'),
    onError: (caught) => setError(caught instanceof Error && !('status' in caught) ? caught.message : describeError(caught)),
  });

  const ready = reason.trim().length >= 5;
  const dirty = !save.isSuccess && (value !== '' || amountMinor !== null || noOwnRate || reason !== '' || reference !== '' || effectiveFrom !== todayIso());

  return (
    <FormScreen
      title={target.title}
      eyebrow={tr('billing', 'doctor_share')}
      icon={Percent}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'new_share')} icon={Percent}>
        <Field label={tr('billing', 'rate_kind')}>
          <ChoiceButtons<ShareRateKind>
            value={kind}
            disabled={noOwnRate}
            onChange={setKind}
            options={[
              { value: 'pct', label: tr('billing', 'pct_of_collected') },
              { value: 'fixed', label: tr('billing', 'fixed_per_unit') },
            ]}
          />
        </Field>
        {kind === 'fixed' ? (
          <Field label={tr('billing', 'amount_per_unit')} hint={tr('billing', 'paid_per_unit_billed_as_the_money_is_collected')}>
            <MoneyInput valueMinor={amountMinor} disabled={noOwnRate} onChangeMinor={setAmountMinor} />
          </Field>
        ) : (
          <Field label={tr('billing', 'share')}>
            <Input inputMode="decimal" value={value} disabled={noOwnRate} onChange={(event) => setValue(event.target.value)} />
          </Field>
        )}
        {target.allowNoOwnRate && (
          <div className="sm:col-span-2">
            <Checkbox
              checked={noOwnRate}
              onCheckedChange={setNoOwnRate}
              label={target.scope === 'ward_rule' ? tr('billing', 'use_the_doctors_own_rate') : tr('billing', 'no_own_rate_from_this_date_contract_ended')}
            />
          </div>
        )}
        <Field label={tr('billing', 'starts_on')} hint={tr('billing', 'today_or_later_bills_already_raised_are_never')}>
          <DatePicker value={effectiveFrom} min={todayIso()} onChange={(next) => setEffectiveFrom(next)} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'reason')} icon={MessageSquare}>
        <Field label={tr('billing', 'reason')} required className="sm:col-span-2">
          <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'contract_reference')} hint={tr('billing', 'optional')}>
          <Input value={reference} onChange={(event) => setReference(event.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}

function CategoryDefaultForm({ returnTo }: { returnTo: string }) {
  const saved = useShareSaved(returnTo);
  const [category, setCategory] = useState<string>(SHAREABLE_CATEGORIES[0]);
  const [value, setValue] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const sharePct = parsePercent(value);
      if (sharePct === null || sharePct < 0 || sharePct > 100) throw new Error('Enter a percentage between 0 and 100');
      return post('/accounts/doctor-share/changes/category-default', { category, sharePct, effectiveFrom, reason: reason.trim() });
    },
    onSuccess: () => saved(false),
    onError: (caught) => setError(caught instanceof Error && !('status' in caught) ? caught.message : describeError(caught)),
  });

  const ready = reason.trim().length >= 5;
  const dirty = !save.isSuccess && (value !== '' || reason !== '' || category !== SHAREABLE_CATEGORIES[0] || effectiveFrom !== todayIso());

  return (
    <FormScreen
      title={tr('billing', 'set_the_default_share_for_a_whole_category')}
      description={tr('billing', 'sets_the_default_for_every_active_service_of')}
      eyebrow={tr('billing', 'doctor_share')}
      icon={Percent}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'new_share')} icon={Percent}>
        <Field label={tr('billing', 'category')}>
          <Select value={category} onChange={(event) => setCategory(event.target.value)}>
            {SHAREABLE_CATEGORIES.map((option) => (
              <option key={option} value={option}>
                {humanize(option)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('billing', 'share')}>
          <Input inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'starts_on')}>
          <DatePicker value={effectiveFrom} min={todayIso()} onChange={(next) => setEffectiveFrom(next)} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'reason')} icon={MessageSquare} columns={1}>
        <Field label={tr('billing', 'reason')} required>
          <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
